/**
 * Part 26, Task 92 — die Freigabe-Warteschlange als reine Funktion.
 *
 * `GET /api/t/:id/rechnungen?status=…` (services/billing.ts, `TenantInvoiceListRow`) liefert je
 * Rechnung den Aussteller, Status, Nummer, Netto, Umsatzsteuer, Brutto, das Dokument, `self_approved`
 * und die Fahne `rueckbuchung` — sie steht dort schon, weil die Abfrage `v_review_queue` nach
 * `kind = 'rechnung_rueckbuchung'` anjoint. Was sie **nicht** liefert, sind Steuerprofil, Nummernserie
 * und Kreditor des Ausstellers; die stehen an `GET /api/t/:id/team` (services/team.ts, `TeamRow`) und
 * werden hier über `tenant_contractor_id` danebengelegt. `GET /provisionen?status=…` liefert die
 * einzelnen Positionen.
 *
 * Diese Datei ordnet die Positionen ihrer Rechnung zu und setzt eine Fahne. Sie **summiert nichts**:
 * eine im Browser gebildete Summe über die angezeigten Positionen kann von `net_cents` abweichen,
 * sobald eine Zeile nach dem Einreichen zurückgebucht wurde — und genau dieser Fall ist der Grund,
 * aus dem es diesen Bildschirm gibt. Keine Umsatzsteuer, keine Nummer, keine Regel.
 */
import type { ProvisionZeile, RechnungListenZeile, RechnungZeile, TeamZeile } from './api'

/** Genau die drei Werte aus `VAT_MODES` (src/shared/schemas/profil.ts), über die Team-Zeile geführt. */
export type Steuerprofil = TeamZeile['vat_mode']

export const STEUERPROFIL_TEXT: Readonly<Record<Steuerprofil, string>> = {
  kleinunternehmer: 'Kleinunternehmer (§ 19 UStG, keine Umsatzsteuer)',
  regelbesteuert: 'Regelbesteuert (19 % Umsatzsteuer)',
  reverse_charge: 'Reverse Charge (§ 13b UStG, Steuerschuldnerschaft des Leistungsempfängers)',
}

/**
 * Die Felder einer Rechnung, die die vier Dialoge und die Zeile „zuletzt entschieden" brauchen.
 * Kein Nachbau: der Typ wird aus `RechnungZeile` (services/billing.ts, `ContractorInvoiceRow`)
 * herausgeschnitten, damit eine Umbenennung am Server hier bricht statt zu driften. Die Antworten
 * von approve/reject/payout/cancel tragen die volle Zeile und passen deshalb ohne Umformung hinein.
 */
export type RechnungsZeile = Pick<
  RechnungZeile,
  'id' | 'status' | 'number' | 'issued_at' | 'due_at' | 'net_cents' | 'vat_cents' | 'gross_cents' | 'self_approved'
>

export interface RechnungsGruppe {
  invoice_id: string
  empfaenger_name: string
  /** Aus der Team-Zeile; `null`, solange der Aussteller dort nicht (mehr) geführt wird. */
  rechnungspraefix: string | null
  steuerprofil: Steuerprofil | null
  profil_geprueft: boolean
  kreditor_no: number | null
  status: RechnungListenZeile['status']
  /**
   * `kind` des Servers. Er entscheidet mehr als ein Etikett: eine Stornorechnung wird mit
   * `status='approved'` angelegt (services/billing.ts, CANCELLATION_INSERT_SQL) und steht damit im
   * selben Reiter wie die Rechnungen, die noch zu überweisen sind — obwohl sie das negative
   * Gegenstück ist, das nie ausgezahlt wird.
   */
  art: RechnungListenZeile['kind']
  nummer: string | null
  issued_at: string | null
  due_at: string | null
  /** unverändert aus der Antwort des Servers — hier wird nicht gerechnet */
  net_cents: number
  vat_cents: number
  gross_cents: number
  /** `self_approved` des Servers: die Rechnung WURDE vom Besitzer ihres Ausstellers freigegeben. */
  selbstfreigabe: boolean
  /** `rueckbuchung` des Servers: hinter der Rechnung wartet eine Zeile `rechnung_rueckbuchung`. */
  rueckbuchung: boolean
  positionen: ProvisionZeile[]
}

export interface GruppierungsEingabe {
  rechnungen: readonly RechnungListenZeile[]
  zeilen: readonly ProvisionZeile[]
  team: readonly TeamZeile[]
}

export function gruppiereRechnungen(eingabe: GruppierungsEingabe): RechnungsGruppe[] {
  const nachTc = new Map(eingabe.team.map((zeile) => [zeile.tenant_contractor_id, zeile]))
  const gruppen = new Map<string, RechnungsGruppe>()

  for (const r of eingabe.rechnungen) {
    const mitglied = nachTc.get(r.tenant_contractor_id)
    gruppen.set(r.id, {
      invoice_id: r.id,
      empfaenger_name: r.empfaenger,
      rechnungspraefix: mitglied?.invoice_prefix ?? null,
      steuerprofil: mitglied?.vat_mode ?? null,
      // Ohne Team-Zeile ist das Profil nicht „geprüft", sondern unbekannt — und unbekannt zählt hier
      // als ungeprüft, weil der Bildschirm dann warnt statt zu beruhigen.
      profil_geprueft: mitglied !== undefined && mitglied.verified_at !== null,
      kreditor_no: mitglied?.kreditor_no ?? null,
      status: r.status,
      art: r.kind,
      nummer: r.number,
      issued_at: r.issued_at,
      due_at: r.due_at,
      net_cents: r.net_cents,
      vat_cents: r.vat_cents,
      gross_cents: r.gross_cents,
      selbstfreigabe: r.self_approved === 1,
      rueckbuchung: r.rueckbuchung === 1,
      positionen: [],
    })
  }

  // Eine Position ohne passende Rechnung wird weggelassen — sie gehört zu einer Rechnung, die der
  // Statusfilter dieser Ansicht nicht zurückgegeben hat.
  for (const zeile of eingabe.zeilen) {
    if (zeile.contractor_invoice_id === null) continue
    gruppen.get(zeile.contractor_invoice_id)?.positionen.push(zeile)
  }

  const liste = [...gruppen.values()]
  for (const gruppe of liste) gruppe.positionen.sort((a, b) => a.ref_no - b.ref_no)
  liste.sort((a, b) => {
    const nachName = a.empfaenger_name.localeCompare(b.empfaenger_name, 'de')
    return nachName !== 0 ? nachName : a.invoice_id.localeCompare(b.invoice_id)
  })
  return liste
}

/** Die Serie, aus der die Freigabe die nächste Nummer zieht — der Präfix, nie die Nummer selbst. */
export function serienName(praefix: string | null, jahr: number): string {
  return praefix === null ? 'unbekannte Serie' : `${praefix}-${jahr}-…`
}
